import {
    RecordingPresets,
    requestRecordingPermissionsAsync,
    setAudioModeAsync,
    useAudioRecorder,
    useAudioRecorderState
} from "expo-audio";
import type * as ExpoFileSystem from "expo-file-system/legacy";
import type * as ExpoHaptics from "expo-haptics";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AppState, type AppStateStatus, Linking, Platform } from "react-native";

import { useAudioSession } from "./useAudioSession";
import { useStableCallback } from "./useStableCallback";
import { useUnmountSafe } from "./useUnmountSafe";

import {
    getAudioUploadUrl,
    getQuestionAudioUploadUrl,
    transcribeQuestionRecording,
    transcribeRecording
} from "@/lib/api";

// ── Types ─────────────────────────────────────────────────────────────────

export interface FloatingMicField {
    id: string;
    label: string;
}

export interface RecorderError {
    msg: string;
    /**
     * Optional action chip the host can render on the Snackbar (e.g.
     * "Open Settings" for permanently-denied mic permission). Bundled with
     * the message in one atomic object — they cannot desync.
     */
    action?: { label: string; onPress: () => void };
}

/**
 * Discriminated union: every phase carries exactly the data it needs and no
 * more. The host uses `state.status` to switch UI; `state.field` exists only
 * where it makes sense; `state.error` exists only in the `error` phase.
 *
 * This shape is the single source of truth for the entire recorder. There
 * are no parallel `errorMsg + errorAction` slots that can desync.
 */
export type RecorderState =
    | { status: "idle" }
    | { status: "recording"; field: FloatingMicField; startedAt: number }
    | { status: "thinking"; field: FloatingMicField }
    | { status: "error"; error: RecorderError };

type RecorderAction =
    | { type: "start"; field: FloatingMicField }
    | { type: "thinking_started"; field: FloatingMicField }
    | { type: "transcribe_succeeded" }
    | { type: "error"; error: RecorderError }
    | { type: "cancel" }
    | { type: "clear_error" };

function reducer(state: RecorderState, action: RecorderAction): RecorderState {
    switch (action.type) {
        case "start":
            // Allow start from idle or error (clearing the error). Ignore otherwise.
            if (state.status !== "idle" && state.status !== "error") {
                return state;
            }
            return { status: "recording", field: action.field, startedAt: Date.now() };
        case "thinking_started":
            if (state.status !== "recording") {
                return state;
            }
            return { status: "thinking", field: action.field };
        case "transcribe_succeeded":
            return { status: "idle" };
        case "error":
            return { status: "error", error: action.error };
        case "cancel":
            return { status: "idle" };
        case "clear_error":
            if (state.status !== "error") {
                return state;
            }
            return { status: "idle" };
        default:
            return state;
    }
}

interface UseQuestionRecorderOptions {
    visitId: string;
    sectionId: string;
    /**
     * Called with the final transcript and the field that was active when
     * `start(field)` was invoked (not the current activeField — focus may
     * have moved during transcription). Wrapped internally with
     * `useStableCallback` so the host is free to pass an inline arrow.
     */
    onTranscribed: (text: string, field: FloatingMicField) => void;
    /**
     * Which API endpoints to use for upload + transcribe.
     *   - "question" (default): per-question audio table. Requires `field.id`
     *     to be a real question id. Used by TextQuestion.
     *   - "section": section-narrative audio table. `field.id` is unused in
     *     API calls (still surfaces in `state.field.label` for the
     *     ThinkingStripe subtitle). Used by NarrativeChapter.
     */
    mode?: "question" | "section";
}

export interface UseQuestionRecorderReturn {
    state: RecorderState;
    /** Live elapsed seconds; only meaningful while recording. */
    seconds: number;
    /** Live audio meter 0..1; only meaningful while recording. */
    meter: number;
    /** Begin a recording for `field`. No-op if not idle/error. */
    start: (field: FloatingMicField) => Promise<void>;
    /** Stop the active recording, transition through thinking, transcribe, then idle. */
    stop: () => Promise<void>;
    /** Discard whatever phase we're in and return to idle. */
    cancel: () => void;
    /** Dismiss the error and return to idle. */
    clearError: () => void;
}

// ── Module setup ──────────────────────────────────────────────────────────

const IS_WEB = Platform.OS === "web";
const MAX_RECORDING_SECONDS = 180;

// eslint-disable-next-line @typescript-eslint/no-require-imports
const FileSystem = IS_WEB ? null : (require("expo-file-system/legacy") as typeof ExpoFileSystem);
// eslint-disable-next-line @typescript-eslint/no-require-imports
const Haptics = IS_WEB ? null : (require("expo-haptics") as typeof ExpoHaptics);

/**
 * Crash-safe wrapper around `MediaRecorder.isTypeSupported`. Older Safari
 * (pre-14.5) and ancient Android WebViews ship MediaRecorder without
 * `isTypeSupported` or with implementations that throw `TypeError` for
 * unknown args. Calling it directly inside a fallback chain would let the
 * first throw kill the entire `start()` flow.
 */
function safeIsTypeSupported(mime: string): boolean {
    if (typeof MediaRecorder === "undefined") {
        return false;
    }
    if (typeof MediaRecorder.isTypeSupported !== "function") {
        return false;
    }
    try {
        return MediaRecorder.isTypeSupported(mime);
    } catch {
        return false;
    }
}

interface WebMime {
    mimeType: string;
    ext: string;
    contentType: string;
}

/**
 * Pick the first MediaRecorder MIME type the browser supports.
 * Safari (desktop + iOS) does NOT support audio/webm — it only does audio/mp4.
 * Chrome/Firefox prefer audio/webm with opus. The empty-string fallback lets
 * the browser pick its own default.
 */
function pickWebMimeType(): WebMime {
    if (typeof MediaRecorder === "undefined") {
        return { mimeType: "", ext: "webm", contentType: "audio/webm" };
    }
    const candidates: WebMime[] = [
        { mimeType: "audio/webm;codecs=opus", ext: "webm", contentType: "audio/webm" },
        { mimeType: "audio/webm", ext: "webm", contentType: "audio/webm" },
        { mimeType: "audio/mp4", ext: "mp4", contentType: "audio/mp4" },
        { mimeType: "audio/aac", ext: "aac", contentType: "audio/aac" }
    ];
    for (const c of candidates) {
        if (safeIsTypeSupported(c.mimeType)) {
            return c;
        }
    }
    return { mimeType: "", ext: "webm", contentType: "audio/webm" };
}

// ── The hook ──────────────────────────────────────────────────────────────

export function useQuestionRecorder({
    visitId,
    sectionId,
    onTranscribed,
    mode = "question"
}: UseQuestionRecorderOptions): UseQuestionRecorderReturn {
    const [state, dispatch] = useReducer(reducer, { status: "idle" });

    // Fast-changing values stay outside the reducer to avoid action spam.
    const [meter, setMeter] = useState(0);
    const [seconds, setSeconds] = useState(0);

    // Lifecycle helpers — survive React 18 StrictMode's double-mount.
    const { isMounted, signal: mountSignal } = useUnmountSafe();

    // Stabilize the parent callback so we can call it from async chains
    // without it being a dep of any effect downstream.
    const stableOnTranscribed = useStableCallback(onTranscribed);

    // iOS audio session is acquired/released as a derived effect of state.
    // Releases on every exit path including thrown catch blocks (no parallel
    // cleanup sites) — kills the music-ducking leak class.
    useAudioSession(state.status === "recording");

    // Refs that own non-state side-data.
    const localUriRef = useRef<string | null>(null);
    const webMimeRef = useRef<WebMime | null>(null);
    const webRecorderRef = useRef<MediaRecorder | null>(null);
    const webStreamRef = useRef<MediaStream | null>(null);
    const webChunksRef = useRef<Blob[]>([]);
    const webAudioCtxRef = useRef<AudioContext | null>(null);
    const webMeterFrameRef = useRef<number | null>(null);
    const stopFnRef = useRef<(() => Promise<void>) | null>(null);
    const cancelFnRef = useRef<(() => void) | null>(null);

    // expo-audio's hooks — the recorder owns the native session, the state
    // hook polls metering at 100ms (the 2-arg callback form of
    // useAudioRecorder lost `metering` from its RecordingStatus payload in
    // recent expo-audio drift; useAudioRecorderState is what useVoiceRecorder
    // already uses successfully).
    const recorder = useAudioRecorder({
        ...RecordingPresets.HIGH_QUALITY,
        isMeteringEnabled: true
    });
    const recorderState = useAudioRecorderState(recorder, 100);

    useEffect(() => {
        if (typeof recorderState.metering === "number") {
            const normalized = Math.max(0, (recorderState.metering + 60) / 60);
            setMeter(normalized);
        }
    }, [recorderState.metering]);

    // ── Web recording cleanup helper ──
    const cleanupWebRecording = useCallback(() => {
        if (webMeterFrameRef.current) {
            cancelAnimationFrame(webMeterFrameRef.current);
            webMeterFrameRef.current = null;
        }
        if (webAudioCtxRef.current) {
            webAudioCtxRef.current.close().catch(() => {});
            webAudioCtxRef.current = null;
        }
        webStreamRef.current?.getTracks().forEach((t) => t.stop());
        webStreamRef.current = null;
        webRecorderRef.current = null;
        webChunksRef.current = [];
    }, []);

    // ── Seconds timer driven by recording state ──
    useEffect(() => {
        if (state.status !== "recording") {
            setSeconds(0);
            return;
        }
        const startedAt = state.startedAt;
        const tick = () => {
            const s = Math.floor((Date.now() - startedAt) / 1000);
            setSeconds(s);
            if (s >= MAX_RECORDING_SECONDS) {
                stopFnRef.current?.();
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [state]);

    // ── Reset meter when not recording ──
    useEffect(() => {
        if (state.status !== "recording") {
            setMeter(0);
        }
    }, [state.status]);

    // ── start ──
    const start = useCallback(
        async (field: FloatingMicField) => {
            if (state.status !== "idle" && state.status !== "error") {
                return;
            }

            try {
                if (IS_WEB) {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    if (!isMounted()) {
                        stream.getTracks().forEach((t) => t.stop());
                        return;
                    }
                    webStreamRef.current = stream;
                    webChunksRef.current = [];

                    const audioCtx = new AudioContext();
                    const source = audioCtx.createMediaStreamSource(stream);
                    const analyser = audioCtx.createAnalyser();
                    analyser.fftSize = 256;
                    source.connect(analyser);
                    webAudioCtxRef.current = audioCtx;

                    const dataArray = new Uint8Array(analyser.frequencyBinCount);
                    const meterLoop = () => {
                        analyser.getByteTimeDomainData(dataArray);
                        let sum = 0;
                        for (let i = 0; i < dataArray.length; i++) {
                            const v = (dataArray[i] - 128) / 128;
                            sum += v * v;
                        }
                        const rms = Math.sqrt(sum / dataArray.length);
                        const normalized = Math.min(1, rms * 3);
                        setMeter(normalized);
                        webMeterFrameRef.current = requestAnimationFrame(meterLoop);
                    };
                    webMeterFrameRef.current = requestAnimationFrame(meterLoop);

                    const mime = pickWebMimeType();
                    webMimeRef.current = mime;
                    const mediaRecorder = mime.mimeType
                        ? new MediaRecorder(stream, { mimeType: mime.mimeType })
                        : new MediaRecorder(stream);
                    mediaRecorder.ondataavailable = (e) => {
                        if (e.data.size > 0) {
                            webChunksRef.current.push(e.data);
                        }
                    };
                    webRecorderRef.current = mediaRecorder;
                    mediaRecorder.start(250);

                    dispatch({ type: "start", field });
                    return;
                }

                const perm = await requestRecordingPermissionsAsync();
                if (!isMounted()) {
                    return;
                }
                if (!perm.granted) {
                    if (perm.canAskAgain === false) {
                        dispatch({
                            type: "error",
                            error: {
                                msg: "Microphone access was denied. Open Settings to enable it for this app.",
                                action: {
                                    label: "Open Settings",
                                    onPress: () => {
                                        Linking.openSettings().catch(() => {});
                                    }
                                }
                            }
                        });
                    } else {
                        dispatch({
                            type: "error",
                            error: { msg: "Microphone access is required to record." }
                        });
                    }
                    return;
                }

                // Audio session must be active BEFORE prepareToRecordAsync —
                // expo-audio fails with "Failed to configure audio session:
                // Session activation failed" otherwise. The useAudioSession
                // hook would activate the session via its derived effect, but
                // effects run after React commits → too late for this synchronous
                // chain. Call imperatively here; the effect's release-on-cleanup
                // safety net still handles every exit path.
                await setAudioModeAsync({ allowsRecording: true, playsInSilentMode: true });
                await recorder.prepareToRecordAsync();
                if (!isMounted()) {
                    return;
                }
                recorder.record();

                if (Haptics) {
                    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
                }

                dispatch({ type: "start", field });
            } catch (e) {
                if (IS_WEB) {
                    cleanupWebRecording();
                    if (e instanceof DOMException && e.name === "NotAllowedError") {
                        dispatch({
                            type: "error",
                            error: {
                                msg: "Microphone access was blocked. Allow it in your browser site settings to record."
                            }
                        });
                        return;
                    }
                }
                dispatch({
                    type: "error",
                    error: { msg: e instanceof Error ? e.message : "Failed to start recording" }
                });
            }
        },
        [state.status, recorder, cleanupWebRecording, isMounted]
    );

    // ── stop ──
    const stop = useCallback(async () => {
        if (state.status !== "recording") {
            return;
        }
        const field = state.field;
        let persistentUri: string;

        try {
            if (IS_WEB) {
                const mediaRecorder = webRecorderRef.current;
                if (!mediaRecorder || mediaRecorder.state === "inactive") {
                    cleanupWebRecording();
                    dispatch({
                        type: "error",
                        error: { msg: "Recording failed — no audio captured." }
                    });
                    return;
                }
                const mime = webMimeRef.current ?? pickWebMimeType();
                persistentUri = await new Promise<string>((resolve, reject) => {
                    mediaRecorder.onstop = () => {
                        const blob = new Blob(webChunksRef.current, { type: mime.contentType });
                        if (blob.size === 0) {
                            reject(new Error("Recording failed — no audio captured."));
                            return;
                        }
                        resolve(URL.createObjectURL(blob));
                    };
                    mediaRecorder.stop();
                });
                cleanupWebRecording();
            } else {
                // CRITICAL: await recorder.stop() — it returns a Promise that
                // resolves AFTER the file is finalized to disk. Reading
                // recorder.uri before it resolves can return null on slow
                // devices, leading to copyAsync ENOENT or 0-byte clips that
                // Whisper transcribes as "".
                await recorder.stop();
                if (!isMounted()) {
                    return;
                }
                const uri = recorder.uri;
                if (!uri) {
                    dispatch({
                        type: "error",
                        error: { msg: "Recording failed — no audio captured." }
                    });
                    return;
                }
                if (!FileSystem) {
                    dispatch({
                        type: "error",
                        error: { msg: "FileSystem unavailable on this platform." }
                    });
                    return;
                }
                const dir = `${FileSystem.documentDirectory}question-recordings/`;
                await FileSystem.makeDirectoryAsync(dir, { intermediates: true });
                persistentUri = `${dir}${visitId}_${sectionId}_${field.id}_${Date.now()}.m4a`;
                await FileSystem.copyAsync({ from: uri, to: persistentUri });
            }

            localUriRef.current = persistentUri;

            if (Haptics) {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy).catch(() => {});
            }

            if (!isMounted() || mountSignal.aborted) {
                return;
            }
            dispatch({ type: "thinking_started", field });

            const mime: WebMime = IS_WEB
                ? (webMimeRef.current ?? pickWebMimeType())
                : { ext: "m4a", contentType: "audio/m4a", mimeType: "audio/m4a" };
            const filename = `recording_${Date.now()}.${mime.ext}`;
            const durationSeconds = Math.floor((Date.now() - state.startedAt) / 1000);

            // Branch on mode — per-question audio uses a different API + table
            // than section-narrative audio. Question endpoints require a real
            // question_id FK; section endpoints are scoped to (visit, section).
            let uploadUrl: string;
            let recordingId: number | null = null;
            let s3Key: string;
            if (mode === "section") {
                const r = await getAudioUploadUrl(visitId, sectionId, filename, mime.contentType);
                uploadUrl = r.uploadUrl;
                s3Key = r.s3Key;
            } else {
                const r = await getQuestionAudioUploadUrl(
                    visitId,
                    sectionId,
                    field.id,
                    filename,
                    mime.contentType
                );
                uploadUrl = r.uploadUrl;
                recordingId = r.recordingId;
                s3Key = r.s3Key;
            }
            if (!isMounted() || mountSignal.aborted) {
                return;
            }

            if (IS_WEB) {
                const blob = await fetch(persistentUri).then((r) => r.blob());
                const uploadRes = await fetch(uploadUrl, {
                    method: "PUT",
                    headers: { "Content-Type": mime.contentType },
                    body: blob,
                    signal: mountSignal
                });
                if (!uploadRes.ok) {
                    throw new Error(`Upload failed: ${uploadRes.status}`);
                }
            } else if (FileSystem) {
                await FileSystem.uploadAsync(uploadUrl, persistentUri, {
                    httpMethod: "PUT",
                    headers: { "Content-Type": mime.contentType }
                });
            }
            if (!isMounted() || mountSignal.aborted) {
                return;
            }

            const { transcript } =
                mode === "section"
                    ? await transcribeRecording(visitId, sectionId, s3Key)
                    : await transcribeQuestionRecording(
                          visitId,
                          sectionId,
                          field.id,
                          recordingId as number,
                          durationSeconds
                      );
            if (!isMounted() || mountSignal.aborted) {
                return;
            }

            stableOnTranscribed(transcript, field);

            if (Haptics) {
                Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => {});
            }

            // Clean up local audio — it lives in S3 now and there's no recovery for v1.
            if (IS_WEB && persistentUri.startsWith("blob:")) {
                URL.revokeObjectURL(persistentUri);
            } else if (!IS_WEB && FileSystem) {
                FileSystem.deleteAsync(persistentUri, { idempotent: true }).catch(() => {});
            }
            localUriRef.current = null;

            dispatch({ type: "transcribe_succeeded" });
        } catch (e) {
            // Audio session release is handled by useAudioSession's cleanup
            // because this dispatch transitions state.status away from "recording".
            if (localUriRef.current) {
                if (IS_WEB && localUriRef.current.startsWith("blob:")) {
                    URL.revokeObjectURL(localUriRef.current);
                } else if (!IS_WEB && FileSystem) {
                    FileSystem.deleteAsync(localUriRef.current, { idempotent: true }).catch(
                        () => {}
                    );
                }
                localUriRef.current = null;
            }
            if (!isMounted()) {
                return;
            }
            dispatch({
                type: "error",
                error: { msg: e instanceof Error ? e.message : "Transcription failed" }
            });
        }
    }, [
        state,
        recorder,
        visitId,
        sectionId,
        mode,
        cleanupWebRecording,
        stableOnTranscribed,
        isMounted,
        mountSignal
    ]);

    stopFnRef.current = stop;

    // ── cancel ──
    const cancel = useCallback(() => {
        if (state.status === "idle") {
            return;
        }

        if (IS_WEB) {
            if (webRecorderRef.current && webRecorderRef.current.state !== "inactive") {
                webRecorderRef.current.onstop = null;
                webRecorderRef.current.stop();
            }
            cleanupWebRecording();
        } else {
            recorder.stop().catch(() => {});
            const tempUri = recorder.uri;
            if (tempUri && FileSystem) {
                FileSystem.deleteAsync(tempUri, { idempotent: true }).catch(() => {});
            }
            if (Haptics) {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
            }
        }
        localUriRef.current = null;

        dispatch({ type: "cancel" });
    }, [state.status, recorder, cleanupWebRecording]);

    cancelFnRef.current = cancel;

    // ── clearError ──
    const clearError = useCallback(() => {
        dispatch({ type: "clear_error" });
    }, []);

    // ── AppState: cancel mid-recording on background / phone call interruption ──
    // iOS phone-call interruption surfaces as AppState `inactive`. Backgrounding
    // and screen lock both surface as `background`. Either case kills the
    // recording (audio session would have been interrupted anyway) and the
    // RN gets a clear "Recording stopped — please tap the mic again to retry"
    // message instead of a silent failure.
    //
    // Thinking phase is allowed to continue — upload + transcribe don't need
    // foreground. The unmount/abort signal handles true component teardown.
    useEffect(() => {
        const handler = (next: AppStateStatus) => {
            // iOS fires "inactive" transiently for system UI like alerts, control
            // center, notification banners, and even keyboard appearance — none of
            // which justify killing a recording. Only "background" (the app is
            // actually pushed off-screen) indicates a real interruption.
            if (next === "background" && state.status === "recording") {
                cancelFnRef.current?.();
                dispatch({
                    type: "error",
                    error: {
                        msg: "Recording stopped — please tap the mic again to retry"
                    }
                });
            }
        };
        const sub = AppState.addEventListener("change", handler);
        return () => sub.remove();
    }, [state.status]);

    return useMemo(
        () => ({ state, seconds, meter, start, stop, cancel, clearError }),
        [state, seconds, meter, start, stop, cancel, clearError]
    );
}
